import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Fab,
  Switch,
  useMediaQuery,
  useTheme as useMuiTheme,
  Autocomplete,
  ListSubheader,
  FormControlLabel,
} from '@mui/material';
import { PrimaryButton, SecondaryButton, ConfirmDialog, EditableSelect, AppIconButton, EmptyState, PageHeroBand } from '../../components/ui';
import CategoryManagerDrawer from '../../components/finance/CategoryManagerDrawer';
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import { formatCurrency } from '../../config/currency';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import Modal from '../../components/Modal';
import TransactionRulesManager from '../../components/finance/TransactionRulesManager';

// --- Types ---

interface DetectedColumn {
  csvHeader: string;
  sampleValues: string[];
  suggestedMapping: string;
  confidence: string;
}

interface DetectResponse {
  detectedFormat: string;
  templateMatch: { id: number; name: string; applyVat?: boolean } | null;
  columns: DetectedColumn[];
  dateFormatSuggestion: string;
  totalRows: number;
  fileHash: string;
  alreadyImported: false | { importedAt: string; expensesCreated: number; incomeCreated: number };
}

interface SoftMatchCandidate {
  recordType: 'EXPENSE' | 'INCOME';
  recordId: number;
  recordDate: string;
  recordAmount: number;
  recordSupplier: string;
  similarity: number;
  reason: string;
  driftAmount?: number | null;
  driftPercent?: number | null;
  driftKind?: 'NORMAL' | 'COMMISSION_BAND' | 'OUT_OF_BAND' | null;
  channelId?: number | null;
  channelName?: string | null;
  expectedCommissionPercent?: number | null;
}

interface AggregatorAliasMatch {
  suggestedAction?: 'RECONCILE' | 'ADJUSTMENT' | null;
  channelId: number;
  channelName: string;
  aliasId: number;
  pattern: string;
}

interface PreviewRow {
  rowIndex: number;
  date: string;
  description: string;
  counterparty: string | null;
  amount: number;
  direction: 'EXPENSE' | 'INCOME';
  bankCategory: string | null;
  suggestedCategoryId: number | null;
  suggestedCategoryName: string | null;
  suggestedIncomeCategory: string | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  matchSource: string;
  possibleDuplicate: boolean;
  duplicateExpenseId: number | null;
  vatAmount: number | null;
  transactionType: string | null;
  // Bank import v2
  externalRef?: string | null;
  alreadyImported?: boolean;
  softMatchCandidates?: SoftMatchCandidate[];
  aggregatorAliasMatch?: AggregatorAliasMatch | null;
}

interface PreviewResponse {
  totalRows: number;
  expenseRows: number;
  incomeRows: number;
  skippedRows: number;
  uncategorizedRows: number;
  rows: PreviewRow[];
}

interface CategoryItem {
  id: number;
  name: string;
  type: string;
  isSystem?: boolean;
}

interface ImportResult {
  importJobId: number;
  expensesCreated: number;
  incomeCreated: number;
  skipped: number;
  rulesLearned: number;
  linkedRecords?: number;
  reconciledRows?: number;
  alreadyImportedSkipped?: number;
}

// Row-level edits in Step 3
interface RowEdit {
  categoryId: number | null;
  incomeCategory: string | null;
  description: string;
  skip: boolean;
  saveAsRule: boolean;
  autoApplied?: boolean;
  // Bank import v2 — per-row action (defaults to IMPORT)
  action?: 'IMPORT' | 'LINK' | 'RECONCILE' | 'ADJUSTMENT' | 'SKIP';
  linkedExpenseId?: number | null;
  linkedIncomeId?: number | null;
  reconcileChannelId?: number | null;
  // When linking with COMMISSION_BAND drift, book the gap as an Aggregator Commission expense.
  bookCommissionExpense?: boolean;
  commissionAmount?: number | null;
  commissionChannelId?: number | null;
  // Aggregator adjustment (refund/chargeback) — bank debit + alias hit
  adjustmentChannelId?: number | null;
  adjustmentKind?: 'REFUND' | 'CHARGEBACK' | 'PROMO' | 'OTHER' | null;
}

const MAPPING_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'description', label: 'Description' },
  { value: 'amount_in', label: 'Amount In' },
  { value: 'amount_out', label: 'Amount Out' },
  { value: 'amount', label: 'Amount (+/-)' },
  { value: 'counterparty', label: 'Counterparty' },
  { value: 'category', label: 'Category' },
  { value: 'reference', label: 'Reference' },
  { value: 'status', label: 'Status' },
  { value: 'tax', label: 'Tax' },
  { value: 'notes', label: 'Notes' },
  { value: 'initiated_by', label: 'Initiated By' },
  { value: 'payment_method', label: 'Payment Method' },
  { value: 'balance', label: 'Balance' },
  { value: 'ignore', label: 'Ignore' },
  { value: '__custom__', label: '+ Custom Label...' },
];

const DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];

const STEPS = ['Upload', 'Map Columns', 'Review', 'Import'];

// --- Component ---

const BankImport: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uncategorizedRef = useRef<HTMLDivElement>(null);

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);

  // Saved templates & transaction rules
  const [savedTemplates, setSavedTemplates] = useState<Array<{ id: number; templateName: string }>>([]);
  const [transactionRules, setTransactionRules] = useState<Array<{ id: number; pattern: string; mappedType: string; mappedCategoryName?: string; mappedIncomeCategory?: string; askEveryTime: boolean }>>([]);
  const [showRulesModal, setShowRulesModal] = useState(false);

  // Step 1 — Upload
  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [detectResult, setDetectResult] = useState<DetectResponse | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Step 2 — Column Mapping
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [applyVat, setApplyVat] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // Step 3 — Category Review
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [rowEdits, setRowEdits] = useState<Record<number, RowEdit>>({});
  const [reviewTab, setReviewTab] = useState<'EXPENSE' | 'INCOME'>('EXPENSE');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [addingCategoryInline, setAddingCategoryInline] = useState(false);
  const [addingIncomeCategoryInline, setAddingIncomeCategoryInline] = useState(false);
  const [newIncomeCategoryName, setNewIncomeCategoryName] = useState('');
  const [customIncomeCategories, setCustomIncomeCategories] = useState<string[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingDescRow, setEditingDescRow] = useState<number | null>(null);
  const [highlightedRows, setHighlightedRows] = useState<Set<number>>(new Set());
  const applySimilarTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 4 — Confirm & Result
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Pagination for review step
  const [reviewPage, setReviewPage] = useState(0);
  const ROWS_PER_PAGE = 25;

  // Re-import confirmation dialog
  const [showReimportDialog, setShowReimportDialog] = useState(false);

  // Category Mapping Wizard (between Step 2 preview and Step 3 review)
  const [showMappingWizard, setShowMappingWizard] = useState(false);

  // Income categories from API
  const [apiIncomeCategories, setApiIncomeCategories] = useState<Array<{ id: number; name: string; displayName: string; isSystem: boolean }>>([]);

  // Category-manager drawer state — shared by expense + income dropdowns.
  const [categoryManager, setCategoryManager] = useState<'EXPENSE' | 'INCOME' | null>(null);

  // ── Editable category CRUD wired into <EditableSelect> ──────────────────
  const handleCreateExpenseCategory = async (name: string): Promise<CategoryItem> => {
    const res = await financeApi.createCategory({ name, isStaffCategory: false, isActive: true });
    const created: CategoryItem = {
      id: res.data.id,
      name: res.data.name,
      type: res.data.type ?? 'EXPENSE',
      isSystem: false,
    };
    setCategories((prev) => [...prev, created]);
    toast.success(`Category "${name}" added`);
    return created;
  };
  const handleRenameExpenseCategory = async (id: number, name: string) => {
    await financeApi.updateCategory(id, { name });
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
    toast.success('Renamed');
  };
  const handleDeleteExpenseCategory = async (id: number) => {
    await financeApi.deleteCategory(id);
    setCategories((prev) => prev.filter((c) => c.id !== id));
    toast.success('Deleted');
  };

  const handleCreateIncomeCategory = async (name: string) => {
    const res = await financeApi.createIncomeCategory({ name, displayName: name, isActive: true });
    const created = {
      id: res.data.id,
      name: res.data.name,
      displayName: res.data.displayName ?? res.data.name,
      isSystem: false,
    };
    setApiIncomeCategories((prev) => [...prev, created]);
    toast.success(`Income category "${name}" added`);
    return { id: res.data.id, label: res.data.displayName ?? res.data.name } as { id: number; label: string };
  };
  const handleRenameIncomeCategory = async (id: number, name: string) => {
    await financeApi.updateIncomeCategory(id, { name, displayName: name });
    setApiIncomeCategories((prev) => prev.map((c) => (c.id === id ? { ...c, name, displayName: name } : c)));
    toast.success('Renamed');
  };
  const handleDeleteIncomeCategory = async (id: number) => {
    await financeApi.deleteIncomeCategory(id);
    setApiIncomeCategories((prev) => prev.filter((c) => c.id !== id));
    toast.success('Deleted');
  };

  // --- Load categories ---
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await financeApi.getCategories();
        const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
        setCategories(data);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    loadCategories();

    // Load income categories from API
    financeApi.getIncomeCategories().then((res) => {
      const data = Array.isArray(res.data) ? res.data : [];
      setApiIncomeCategories(data);
    }).catch((err) => {
      console.error('Error loading income categories:', err);
    });

    // Load saved templates and transaction rules
    financeApi.getCsvTemplates().then((res) => {
      setSavedTemplates(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
    financeApi.getTransactionRules().then((res) => {
      setTransactionRules(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  // --- Shared Styles ---

  const cardSx = {
    p: { xs: 2, sm: 3 },
    borderRadius: '12px',
    backgroundColor: theme.components?.card?.background || '#FFFFFF',
    border: `1px solid ${theme.components?.card?.border || '#E8E6E1'}`,
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#E8E6E1' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#6B6B6B' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#E8E6E1' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };


  // --- Step 1: Upload handlers ---

  const handleFileSelect = (selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only CSV files are supported');
      return;
    }
    setFile(selectedFile);
    setDetectResult(null);
    toast.success(`Selected: ${selectedFile.name}`);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    const toastId = toast.loading('Analyzing format...');
    try {
      const res = await financeApi.uploadBankStatement(file);
      const data: DetectResponse = res.data;
      setDetectResult(data);

      // Pre-fill column mappings
      const mappings: Record<string, string> = {};
      data.columns.forEach((col) => {
        mappings[col.csvHeader] = col.suggestedMapping || 'ignore';
      });
      setColumnMappings(mappings);
      if (data.dateFormatSuggestion) setDateFormat(data.dateFormatSuggestion);
      if (data.templateMatch) {
        setApplyVat(Boolean(data.templateMatch.applyVat));
      } else {
        setApplyVat(false);
      }

      toast.success(`Detected ${data.totalRows} rows`, { id: toastId });

      // Auto-advance if no duplicate warning, otherwise show modal
      if (data.alreadyImported) {
        setShowReimportDialog(true);
      } else {
        setActiveStep(1);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to analyze file';
      toast.error(msg, { id: toastId });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReimportAnyway = () => {
    setShowReimportDialog(false);
    setActiveStep(1);
  };

  const handleReimportCancel = () => {
    setShowReimportDialog(false);
    handleReset();
  };

  // --- Step 2: Column Mapping handlers ---

  // Track which column is getting a custom label
  const [customLabelFor, setCustomLabelFor] = useState<string | null>(null);
  const [customLabelText, setCustomLabelText] = useState('');
  // Track vendor-added custom mapping options
  const [extraMappingOptions, setExtraMappingOptions] = useState<Array<{ value: string; label: string }>>([]);

  const handleMappingChange = (csvHeader: string, mapping: string) => {
    if (mapping === '__custom__') {
      setCustomLabelFor(csvHeader);
      setCustomLabelText('');
      return;
    }
    setColumnMappings((prev) => ({ ...prev, [csvHeader]: mapping }));
  };

  const handleCustomLabelSave = () => {
    if (!customLabelFor || !customLabelText.trim()) return;
    const value = `custom:${customLabelText.trim().toLowerCase().replace(/\s+/g, '_')}`;
    // Add to extra options so it appears in all dropdowns
    setExtraMappingOptions((prev) => {
      if (prev.some((o) => o.value === value)) return prev;
      return [...prev, { value, label: customLabelText.trim() }];
    });
    setColumnMappings((prev) => ({ ...prev, [customLabelFor]: value }));
    setCustomLabelFor(null);
    setCustomLabelText('');
  };

  const canProceedStep2 = useMemo(() => {
    const mappedValues = Object.values(columnMappings);
    const hasDate = mappedValues.includes('date');
    const hasAmount = mappedValues.includes('amount_in') || mappedValues.includes('amount_out') || mappedValues.includes('amount');
    return hasDate && hasAmount;
  }, [columnMappings]);

  // Detect duplicate column mappings (excluding 'ignore' and 'reference' which can appear twice)
  const duplicateMappings = useMemo(() => {
    const exemptFields = new Set(['ignore', 'reference']);
    const seen: Record<string, string[]> = {};
    Object.entries(columnMappings).forEach(([csvHeader, mapping]) => {
      if (exemptFields.has(mapping)) return;
      if (!seen[mapping]) seen[mapping] = [];
      seen[mapping].push(csvHeader);
    });
    const dupes: Set<string> = new Set();
    Object.entries(seen).forEach(([, headers]) => {
      if (headers.length > 1) headers.forEach((h) => dupes.add(h));
    });
    return dupes;
  }, [columnMappings]);

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    const toastId = toast.loading('Generating preview...');
    try {
      // Find status column and its filter value
      const statusCol = Object.entries(columnMappings).find(([, v]) => v === 'status');
      const statusFilter = statusCol ? 'Cleared' : undefined;

      const fileHash = detectResult?.fileHash;
      if (!fileHash) { toast.error('File hash missing. Please re-upload.'); return; }
      const res = await financeApi.previewImport(fileHash, columnMappings, dateFormat, statusFilter, applyVat);
      const data: PreviewResponse = res.data;
      setPreviewData(data);

      // Initialize row edits — pick default action from preview hints.
      const edits: Record<number, RowEdit> = {};
      data.rows.forEach((row) => {
        let defaultAction: RowEdit['action'] = 'IMPORT';
        let defaultExtras: Partial<RowEdit> = {};
        const alias = row.aggregatorAliasMatch;
        const candidates = row.softMatchCandidates ?? [];
        if (alias?.suggestedAction === 'RECONCILE') {
          // Multi-day lump payouts auto-route to settlement.
          // If a single candidate fully covers the payment within NORMAL band, prefer LINK.
          const coverer = candidates.find(
            (c) => c.driftKind === 'NORMAL' && Math.abs(c.recordAmount - row.amount) < 0.01,
          );
          if (coverer) {
            defaultAction = 'LINK';
            defaultExtras = {
              linkedIncomeId: coverer.recordType === 'INCOME' ? coverer.recordId : null,
            };
          } else {
            defaultAction = 'RECONCILE';
            defaultExtras = { reconcileChannelId: alias.channelId };
          }
        } else if (alias?.suggestedAction === 'ADJUSTMENT') {
          defaultAction = 'ADJUSTMENT';
          defaultExtras = {
            adjustmentChannelId: alias.channelId,
            adjustmentKind: 'REFUND',
          };
        }
        edits[row.rowIndex] = {
          categoryId: row.suggestedCategoryId,
          incomeCategory: row.suggestedIncomeCategory,
          description: row.description,
          skip: false,
          saveAsRule: false,
          action: defaultAction,
          ...defaultExtras,
        };
      });
      setRowEdits(edits);

      // Save template if checked
      if (saveTemplate && templateName.trim()) {
        try {
          await financeApi.saveCsvTemplate({
            templateName: templateName.trim(),
            columnMappings,
            dateFormat,
            applyVat,
          });
          toast.success('Template saved');
        } catch {
          toast.error('Failed to save template');
        }
      }

      // Refresh categories — backend seeds defaults on first preview for new vendors
      const prevCatCount = categories.length;
      const prevIncomeCatCount = apiIncomeCategories.length;
      financeApi.getCategories().then((catRes) => {
        const catData = Array.isArray(catRes.data) ? catRes.data : catRes.data?.content || [];
        setCategories(catData);
        if (catData.length > prevCatCount) {
          toast.success(`${catData.length - prevCatCount} default expense categories added`);
        }
      }).catch(() => {});
      financeApi.getIncomeCategories().then((catRes) => {
        const catData = Array.isArray(catRes.data) ? catRes.data : [];
        setApiIncomeCategories(catData);
        if (catData.length > prevIncomeCatCount) {
          toast.success(`${catData.length - prevIncomeCatCount} default income categories added`);
        }
      }).catch(() => {});

      toast.success(`Preview: ${data.expenseRows} expenses, ${data.incomeRows} income`, { id: toastId });
      setShowMappingWizard(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to generate preview';
      toast.error(msg, { id: toastId });
    } finally {
      setPreviewing(false);
    }
  };

  // --- Category Mapping Wizard ---

  /** Normalize a counterparty/description into a grouping pattern */
  const normalizePattern = (text: string): string => {
    const pattern = text
      .replace(/ref:?\s*\S+/gi, '')       // remove "ref: XXXXX"
      .replace(/\d{10,}/g, '')            // remove long numbers (refs, phone numbers)
      .replace(/\s*-\s*\d+.*$/, '')       // remove "- 123 ..."
      .replace(/\s{2,}/g, ' ')            // collapse spaces
      .trim();
    // Take first meaningful part (before address / secondary info)
    const parts = pattern.split(/\s*[-\u2013]\s*/);
    return parts[0]?.trim() || pattern;
  };

  interface WizardGroup {
    pattern: string;
    rows: PreviewRow[];
    totalAmount: number;
    direction: 'EXPENSE' | 'INCOME';
    currentCategoryId: number | null;
    currentIncomeCategory: string | null;
    isUncategorized: boolean;
    saveAsRule: boolean;
  }

  /** Compute grouped patterns for the wizard modal (memoized) */
  const wizardGroups = useMemo<WizardGroup[]>(() => {
    if (!previewData || !showMappingWizard) return [];

    const groupMap = new Map<string, WizardGroup>();

    previewData.rows.forEach((row) => {
      const raw = row.counterparty || row.description || '';
      const pattern = normalizePattern(raw) || raw;
      const key = `${pattern.toUpperCase()}__${row.direction}`;
      const edit = rowEdits[row.rowIndex];

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          pattern,
          rows: [],
          totalAmount: 0,
          direction: row.direction,
          currentCategoryId: edit?.categoryId ?? row.suggestedCategoryId,
          currentIncomeCategory: edit?.incomeCategory ?? row.suggestedIncomeCategory,
          isUncategorized: row.direction === 'EXPENSE'
            ? !(edit?.categoryId ?? row.suggestedCategoryId)
            : !(edit?.incomeCategory ?? row.suggestedIncomeCategory),
          saveAsRule: false,
        });
      }

      const group = groupMap.get(key)!;
      group.rows.push(row);
      group.totalAmount += row.amount;
    });

    // Sort: uncategorized first, then by transaction count descending
    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.isUncategorized !== b.isUncategorized) return a.isUncategorized ? -1 : 1;
      return b.rows.length - a.rows.length;
    });
  }, [previewData, showMappingWizard, rowEdits]);

  /** Track wizard-level category overrides (keyed by pattern+direction) */
  const [wizardOverrides, setWizardOverrides] = useState<
    Record<string, { categoryId?: number; incomeCategory?: string; saveAsRule: boolean }>
  >({});

  /** Reset overrides when wizard opens */
  useEffect(() => {
    if (showMappingWizard) {
      setWizardOverrides({});
    }
  }, [showMappingWizard]);

  const handleWizardCategoryChange = (group: WizardGroup, categoryId: number) => {
    if (!Number.isFinite(categoryId)) return; // ignore "Manage…" sentinel clicks
    const key = `${group.pattern.toUpperCase()}__${group.direction}`;
    setWizardOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], categoryId, saveAsRule: prev[key]?.saveAsRule ?? false },
    }));
  };

  const handleWizardIncomeCategoryChange = (group: WizardGroup, incomeCategory: string) => {
    if (incomeCategory.startsWith('__')) return; // ignore "Manage…" sentinel
    const key = `${group.pattern.toUpperCase()}__${group.direction}`;
    setWizardOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], incomeCategory, saveAsRule: prev[key]?.saveAsRule ?? false },
    }));
  };

  const handleWizardRuleToggle = (group: WizardGroup) => {
    const key = `${group.pattern.toUpperCase()}__${group.direction}`;
    setWizardOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], saveAsRule: !(prev[key]?.saveAsRule ?? false) },
    }));
  };

  /** Apply all wizard overrides to rowEdits, optionally saving rules, then proceed */
  const handleWizardApply = async () => {
    // Apply category overrides to all matching rows
    setRowEdits((prev) => {
      const next = { ...prev };
      wizardGroups.forEach((group) => {
        const key = `${group.pattern.toUpperCase()}__${group.direction}`;
        const override = wizardOverrides[key];
        if (!override) return;

        group.rows.forEach((row) => {
          if (!next[row.rowIndex]) return;
          if (group.direction === 'EXPENSE' && override.categoryId != null) {
            next[row.rowIndex] = { ...next[row.rowIndex], categoryId: override.categoryId };
          }
          if (group.direction === 'INCOME' && override.incomeCategory) {
            next[row.rowIndex] = { ...next[row.rowIndex], incomeCategory: override.incomeCategory };
          }
          if (override.saveAsRule) {
            next[row.rowIndex] = { ...next[row.rowIndex], saveAsRule: true };
          }
        });
      });
      return next;
    });

    // Save rules for checked groups
    const rulesToSave = wizardGroups.filter((g) => {
      const key = `${g.pattern.toUpperCase()}__${g.direction}`;
      return wizardOverrides[key]?.saveAsRule;
    });

    for (const group of rulesToSave) {
      const key = `${group.pattern.toUpperCase()}__${group.direction}`;
      const override = wizardOverrides[key];
      try {
        await financeApi.createTransactionRule({
          pattern: group.pattern,
          mappedType: group.direction,
          mappedCategoryName: group.direction === 'EXPENSE'
            ? categories.find((c) => c.id === override?.categoryId)?.name
            : undefined,
          mappedIncomeCategory: group.direction === 'INCOME'
            ? override?.incomeCategory
            : undefined,
          askEveryTime: false,
        });
      } catch {
        // Silent — rule save is best-effort
      }
    }

    if (rulesToSave.length > 0) {
      toast.success(`${rulesToSave.length} rule${rulesToSave.length > 1 ? 's' : ''} saved`);
      // Refresh rules list
      financeApi.getTransactionRules().then((res) => {
        setTransactionRules(Array.isArray(res.data) ? res.data : []);
      }).catch(() => {});
    }

    setShowMappingWizard(false);
    setActiveStep(2);
  };

  const handleWizardSkip = () => {
    setShowMappingWizard(false);
    setActiveStep(2);
  };

  // --- Step 3: Review handlers ---

  const filteredRows = useMemo(() => {
    if (!previewData) return [];
    return previewData.rows.filter((r) => r.direction === reviewTab);
  }, [previewData, reviewTab]);

  // Reset pagination when tab changes
  useEffect(() => {
    setReviewPage(0);
  }, [reviewTab]);

  // Paginated rows for the current page
  const paginatedRows = useMemo(() => {
    const start = reviewPage * ROWS_PER_PAGE;
    return filteredRows.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRows, reviewPage]);

  const expenseCount = previewData?.rows.filter((r) => r.direction === 'EXPENSE').length || 0;
  const incomeCount = previewData?.rows.filter((r) => r.direction === 'INCOME').length || 0;

  // Overall summary for the top summary card
  const overallSummary = useMemo(() => {
    if (!previewData) return { expenseTotal: 0, incomeTotal: 0, skipped: 0 };
    const expenseTotal = previewData.rows
      .filter((r) => r.direction === 'EXPENSE' && !rowEdits[r.rowIndex]?.skip)
      .reduce((sum, r) => sum + r.amount, 0);
    const incomeTotal = previewData.rows
      .filter((r) => r.direction === 'INCOME' && !rowEdits[r.rowIndex]?.skip)
      .reduce((sum, r) => sum + r.amount, 0);
    const skipped = previewData.rows.filter((r) => rowEdits[r.rowIndex]?.skip).length;
    return { expenseTotal, incomeTotal, skipped };
  }, [previewData, rowEdits]);

  const summaryStats = useMemo(() => {
    if (!previewData) return { total: 0, uncategorized: 0, duplicates: 0 };
    const currentRows = previewData.rows.filter((r) => r.direction === reviewTab);
    const total = currentRows.reduce((sum, r) => sum + r.amount, 0);
    const uncategorized = currentRows.filter((r) => {
      const edit = rowEdits[r.rowIndex];
      if (edit?.skip) return false;
      if (r.direction === 'EXPENSE') return !edit?.categoryId;
      return !edit?.incomeCategory;
    }).length;
    const duplicates = currentRows.filter((r) => r.possibleDuplicate).length;
    return { total, uncategorized, duplicates };
  }, [previewData, reviewTab, rowEdits]);

  const totalUncategorized = useMemo(() => {
    if (!previewData) return 0;
    return previewData.rows.filter((r) => {
      const edit = rowEdits[r.rowIndex];
      if (edit?.skip) return false;
      if (r.direction === 'EXPENSE') return !edit?.categoryId;
      return !edit?.incomeCategory;
    }).length;
  }, [previewData, rowEdits]);

  const setRowAction = (
    rowIndex: number,
    action: 'IMPORT' | 'LINK' | 'RECONCILE' | 'ADJUSTMENT' | 'SKIP',
    extras?: {
      linkedExpenseId?: number;
      linkedIncomeId?: number;
      reconcileChannelId?: number;
      bookCommissionExpense?: boolean;
      commissionAmount?: number;
      commissionChannelId?: number;
      adjustmentChannelId?: number;
      adjustmentKind?: 'REFUND' | 'CHARGEBACK' | 'PROMO' | 'OTHER';
    },
  ) => {
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        action,
        skip: action === 'SKIP',
        linkedExpenseId: extras?.linkedExpenseId ?? null,
        linkedIncomeId: extras?.linkedIncomeId ?? null,
        reconcileChannelId: extras?.reconcileChannelId ?? null,
        bookCommissionExpense: extras?.bookCommissionExpense ?? false,
        commissionAmount: extras?.commissionAmount ?? null,
        commissionChannelId: extras?.commissionChannelId ?? null,
        adjustmentChannelId: extras?.adjustmentChannelId ?? null,
        adjustmentKind: extras?.adjustmentKind ?? null,
      },
    }));
  };

  const handleRowCategoryChange = (rowIndex: number, categoryId: number) => {
    if (!Number.isFinite(categoryId)) return; // ignore "Manage…" sentinel clicks
    // Update the changed row immediately (manual = not autoApplied)
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], categoryId, autoApplied: false },
    }));

    // Fire apply-similar in background
    if (!previewData) return;
    const sourceRow = previewData.rows.find((r) => r.rowIndex === rowIndex);
    if (!sourceRow) return;

    const uncategorizedRows = previewData.rows
      .filter((r) => {
        if (r.rowIndex === rowIndex || r.direction !== 'EXPENSE') return false;
        const edit = rowEdits[r.rowIndex];
        return !edit?.categoryId && !edit?.skip;
      })
      .map((r) => ({ rowIndex: r.rowIndex, description: r.description, counterparty: r.counterparty }));

    if (uncategorizedRows.length === 0) return;

    if (applySimilarTimer.current) clearTimeout(applySimilarTimer.current);
    applySimilarTimer.current = setTimeout(() => {
    financeApi.applySimilar({
      sourceCounterparty: sourceRow.counterparty,
      sourceDescription: sourceRow.description,
      direction: 'EXPENSE',
      categoryId,
      uncategorizedRows,
    }).then((res) => {
      const { matchedRowIndices, matchedCount } = res.data;
      if (matchedCount > 0) {
        setRowEdits((prev) => {
          const next = { ...prev };
          matchedRowIndices.forEach((idx) => {
            if (next[idx]) {
              next[idx] = { ...next[idx], categoryId, autoApplied: true };
            }
          });
          return next;
        });
        setHighlightedRows(new Set(matchedRowIndices));
        setTimeout(() => setHighlightedRows(new Set()), 1500);
        toast.success(`Category applied to ${matchedCount} similar transaction${matchedCount > 1 ? 's' : ''}`);
      }
    }).catch(() => {
      // Silent — manual change already applied, auto-match is best-effort
    });
    }, 300);
  };

  const handleRowIncomeCategoryChange = (rowIndex: number, incomeCategory: string) => {
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], incomeCategory, autoApplied: false },
    }));

    // Fire apply-similar in background for income
    if (!previewData) return;
    const sourceRow = previewData.rows.find((r) => r.rowIndex === rowIndex);
    if (!sourceRow) return;

    const uncategorizedRows = previewData.rows
      .filter((r) => {
        if (r.rowIndex === rowIndex || r.direction !== 'INCOME') return false;
        const edit = rowEdits[r.rowIndex];
        return !edit?.incomeCategory && !edit?.skip;
      })
      .map((r) => ({ rowIndex: r.rowIndex, description: r.description, counterparty: r.counterparty }));

    if (uncategorizedRows.length === 0) return;

    if (applySimilarTimer.current) clearTimeout(applySimilarTimer.current);
    applySimilarTimer.current = setTimeout(() => {
    financeApi.applySimilar({
      sourceCounterparty: sourceRow.counterparty,
      sourceDescription: sourceRow.description,
      direction: 'INCOME',
      incomeCategory,
      uncategorizedRows,
    }).then((res) => {
      const { matchedRowIndices, matchedCount } = res.data;
      if (matchedCount > 0) {
        setRowEdits((prev) => {
          const next = { ...prev };
          matchedRowIndices.forEach((idx) => {
            if (next[idx]) {
              next[idx] = { ...next[idx], incomeCategory, autoApplied: true };
            }
          });
          return next;
        });
        setHighlightedRows(new Set(matchedRowIndices));
        setTimeout(() => setHighlightedRows(new Set()), 1500);
        toast.success(`Category applied to ${matchedCount} similar transaction${matchedCount > 1 ? 's' : ''}`);
      }
    }).catch(() => {
      // Silent — manual change already applied
    });
    }, 300);
  };

  const handleRowSkipToggle = (rowIndex: number) => {
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], skip: !prev[rowIndex]?.skip },
    }));
  };

  const handleRowRuleToggle = (rowIndex: number) => {
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], saveAsRule: !prev[rowIndex]?.saveAsRule },
    }));
  };

  const handleRowDescriptionEdit = (rowIndex: number, description: string) => {
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], description },
    }));
  };

  const handleSelectRow = (rowIndex: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const handleBulkCategory = (categoryId: number) => {
    setRowEdits((prev) => {
      const next = { ...prev };
      selectedRows.forEach((idx) => {
        if (next[idx]) next[idx] = { ...next[idx], categoryId };
      });
      return next;
    });
    setSelectedRows(new Set());
    toast.success(`Category set for ${selectedRows.size} rows`);
  };

  const handleBulkSkip = () => {
    setRowEdits((prev) => {
      const next = { ...prev };
      selectedRows.forEach((idx) => {
        if (next[idx]) next[idx] = { ...next[idx], skip: true };
      });
      return next;
    });
    setSelectedRows(new Set());
    toast.success(`Skipped ${selectedRows.size} rows`);
  };

  const handleAddCategoryInline = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await financeApi.createCategory({ name: newCategoryName.trim(), type: 'EXPENSE' });
      toast.success(`Category "${newCategoryName.trim()}" created`);
      const res = await financeApi.getCategories();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setCategories(data);
      setNewCategoryName('');
      setAddingCategoryInline(false);
    } catch {
      toast.error('Failed to create category');
    }
  };

  const scrollToUncategorized = () => {
    uncategorizedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // --- Step 4: Import handlers ---

  const handleConfirmImport = async () => {
    if (!previewData || !detectResult || !file) return;
    setImporting(true);
    setImportProgress(10);
    const toastId = toast.loading('Importing transactions...');

    try {
      const rows = previewData.rows.map((r) => {
        const edit = rowEdits[r.rowIndex] || {};
        // Default action: SKIP if row is already-imported (Tier 1) or user chose
        // Skip; otherwise the action chosen in the dedupe / aggregator banner,
        // or the legacy IMPORT path.
        const action: 'IMPORT' | 'LINK' | 'RECONCILE' | 'ADJUSTMENT' | 'SKIP' = r.alreadyImported
          ? 'SKIP'
          : (edit.action ?? (edit.skip ? 'SKIP' : 'IMPORT'));
        return {
          rowIndex: r.rowIndex,
          date: r.date,
          description: edit.description || r.description,
          counterparty: r.counterparty,
          amount: r.amount,
          vatAmount: r.vatAmount,
          direction: r.direction,
          categoryId: edit.categoryId,
          incomeCategory: edit.incomeCategory,
          skip: edit.skip || action === 'SKIP',
          saveAsRule: edit.saveAsRule || false,
          action,
          externalRef: r.externalRef ?? null,
          linkedExpenseId: edit.linkedExpenseId ?? null,
          linkedIncomeId: edit.linkedIncomeId ?? null,
          reconcileChannelId: edit.reconcileChannelId ?? null,
          bookCommissionExpense: edit.bookCommissionExpense ?? false,
          commissionAmount: edit.commissionAmount ?? null,
          commissionChannelId: edit.commissionChannelId ?? null,
          adjustmentChannelId: edit.adjustmentChannelId ?? null,
          adjustmentKind: edit.adjustmentKind ?? null,
        };
      });

      setImportProgress(30);

      const res = await financeApi.confirmBankImport({
        fileHash: detectResult.fileHash,
        fileName: file.name,
        bankFormat: detectResult.detectedFormat,
        rows,
      });

      setImportProgress(100);
      setImportResult(res.data as ImportResult);
      toast.success('Import complete!', { id: toastId });
      setActiveStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      toast.error(msg, { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setDetectResult(null);
    setColumnMappings({});
    setPreviewData(null);
    setRowEdits({});
    setImportResult(null);
    setSelectedRows(new Set());
    setImportProgress(0);
    setShowMappingWizard(false);
    setWizardOverrides({});
  };

  // --- Confidence Badge ---

  const ConfidenceBadge = ({ confidence, autoApplied, rowIndex }: { confidence: string; autoApplied?: boolean; rowIndex?: number }) => {
    const colors = {
      HIGH: { bg: (theme.success || '#16A34A') + '20', text: theme.success || '#16A34A', label: 'Matched', border: 'none' },
      MEDIUM: { bg: (theme.warning || '#D97706') + '20', text: theme.warning || '#D97706', label: 'Suggested', border: 'none' },
      LOW: { bg: (theme.danger || '#DC2626') + '20', text: theme.danger || '#DC2626', label: 'Uncategorized', border: 'none' },
      AUTO: { bg: 'color-mix(in srgb, var(--status-info) 9%, transparent)', text: 'var(--status-info)', label: 'Auto', border: '1.5px dashed color-mix(in srgb, var(--status-info) 38%, transparent)' },
    };
    const c = autoApplied ? colors.AUTO : (colors[confidence as keyof typeof colors] || colors.LOW);
    return (
      <Box
        data-testid={rowIndex !== undefined ? (autoApplied ? `auto-matched-badge-${rowIndex}` : `confidence-badge-${rowIndex}`) : undefined}
        sx={{
          display: 'inline-flex',
          px: 1,
          py: 0.25,
          borderRadius: '6px',
          backgroundColor: c.bg,
          color: c.text,
          fontSize: '0.7rem',
          fontWeight: 700,
          whiteSpace: 'nowrap',
          transition: 'all 0.25s ease',
          border: c.border,
        }}
      >
        {c.label}
      </Box>
    );
  };

  // --- Render Step 1: Upload ---

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Saved Templates + Rules Quick Access */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {savedTemplates.length > 0 && (
          <>
            <Typography variant="body2" sx={{ color: theme.gray?.[600] || '#8A8A8A', fontSize: '0.813rem', fontWeight: 600 }}>
              Saved templates:
            </Typography>
            {savedTemplates.map((t: { id: number; templateName: string }) => (
              <Chip
                key={t.id}
                label={t.templateName}
                onClick={() => toast.success(`Template "${t.templateName}" will be auto-applied on upload`)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  backgroundColor: theme.button.primary.bg,
                  color: theme.button.primary.text,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: theme.button.primary.hover },
                }}
              />
            ))}
          </>
        )}
        <button
          onClick={() => setShowRulesModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 8,
            border: `1.5px solid ${theme.gray?.[300] || '#D4D1CC'}`, backgroundColor: theme.components?.card?.background || '#FFFFFF',
            color: theme.text, fontWeight: 600, fontSize: '0.75rem',
            cursor: 'pointer', fontFamily: 'inherit', marginLeft: 'auto',
          }}
        >
          <Cog6ToothIcon style={{ width: 16, height: 16 }} />
          Manage Rules ({transactionRules.length})
        </button>
      </Box>

      {/* Drag & Drop Zone / Compact File Strip */}
      {!file ? (
        <Paper
          data-testid="bank-import-upload"
          sx={{
            ...cardSx,
            border: dragOver
              ? `2px dashed ${theme.primary}`
              : `2px dashed ${theme.gray?.[300] || '#D4D1CC'}`,
            backgroundColor: dragOver ? `${theme.primary}08` : (theme.components?.card?.background || '#FFFFFF'),
            textAlign: 'center',
            py: { xs: 5, sm: 8 },
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme.primary,
              backgroundColor: `${theme.primary}05`,
            },
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          elevation={0}
        >
          <ArrowUpTrayIcon style={{ width: 48, height: 48, color: theme.gray?.[400] || '#8A8A8A', margin: '0 auto 16px' }} />
          <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '1rem', mb: 0.5 }}>
            Drop CSV here or click to browse
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.813rem' }}>
            Supports bank statements from Tide, HDFC, ICICI, and custom CSV formats
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
              e.target.value = '';
            }}
          />
        </Paper>
      ) : (
        <Paper
          data-testid="bank-import-upload"
          sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1.5 }}
          elevation={0}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <DocumentTextIcon style={{ width: 22, height: 22, color: theme.primary }} />
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B', flexShrink: 0 }}>
              ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          </Box>
          <button
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            style={{
              background: 'none', border: 'none', color: theme.primary,
              fontWeight: 600, fontSize: '0.813rem', cursor: 'pointer',
              textDecoration: 'underline', fontFamily: 'inherit', padding: '4px 8px',
            }}
          >
            Change
          </button>
          <PrimaryButton
            onClick={handleAnalyze}
            disabled={analyzing}
            loading={analyzing}
            size="small"
          >
            {analyzing ? 'Analyzing...' : 'Analyze'}
          </PrimaryButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
              e.target.value = '';
            }}
          />
        </Paper>
      )}
    </Box>
  );

  // --- Render Step 2: Column Mapping ---

  const renderStep2 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Date Format */}
      <Paper sx={cardSx} elevation={0}>
        <Typography sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem', mb: 1.5 }}>
          Date Format
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {DATE_FORMATS.map((fmt) => (
            <Chip
              key={fmt}
              label={fmt}
              onClick={() => setDateFormat(fmt)}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 32,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: dateFormat === fmt ? `${theme.primary}22` : (theme.gray?.[100] || '#F0EDE8'),
                color: dateFormat === fmt ? theme.primary : (theme.gray?.[500] || '#6B6B6B'),
                border: dateFormat === fmt ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                '&:hover': { backgroundColor: dateFormat === fmt ? `${theme.primary}30` : (theme.gray?.[200] || '#E8E6E1') },
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* Column Mapping */}
      {isMobile ? (
        // Mobile: cards
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {detectResult?.columns.map((col) => (
            <Paper
              key={col.csvHeader}
              sx={{
                ...cardSx,
                opacity: columnMappings[col.csvHeader] === 'ignore' ? 0.5 : 1,
              }}
              elevation={0}
            >
              <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '0.875rem', mb: 0.5 }}>
                {col.csvHeader}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.gray?.[500] || '#6B6B6B',
                  fontSize: '0.75rem',
                  mb: 1.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {col.sampleValues[0] || '(empty)'}
              </Typography>
              <FormControl fullWidth size="small" sx={inputSx}>
                <InputLabel>Map to</InputLabel>
                <Select
                  data-testid={`column-map-${col.csvHeader}`}
                  value={columnMappings[col.csvHeader] || 'ignore'}
                  label="Map to"
                  onChange={(e) => handleMappingChange(col.csvHeader, e.target.value)}
                  sx={selectSx}
                >
                  {MAPPING_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={opt.value === '__custom__' ? { color: theme.primary, fontWeight: 600, borderTop: `1px solid ${theme.gray?.[200] || '#E8E6E1'}`, mt: 0.5 } : {}}>{opt.label}</MenuItem>
                  ))}
                  {extraMappingOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontStyle: 'italic' }}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {duplicateMappings.has(col.csvHeader) && (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75,
                  px: 0.75, py: 0.25, borderRadius: '6px',
                  backgroundColor: (theme.warning || '#D97706') + '20',
                  color: theme.warning || '#D97706',
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  <ExclamationTriangleIcon style={{ width: 12, height: 12 }} />
                  Same field mapped twice
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      ) : (
        // Desktop: table
        <Paper sx={cardSx} elevation={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.75rem' }}>CSV Column</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.75rem' }}>Sample Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.75rem', minWidth: 180 }}>Mapping</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detectResult?.columns.map((col) => (
                  <TableRow
                    key={col.csvHeader}
                    sx={{
                      opacity: columnMappings[col.csvHeader] === 'ignore' ? 0.5 : 1,
                      '&:hover': { backgroundColor: `${theme.primary}04` },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem' }}>
                      {col.csvHeader}
                    </TableCell>
                    <TableCell sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {col.sampleValues[0] || '(empty)'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <FormControl size="small" sx={{ ...inputSx, flex: 1 }}>
                          <Select
                            data-testid={`column-map-${col.csvHeader}`}
                            value={columnMappings[col.csvHeader] || 'ignore'}
                            onChange={(e) => handleMappingChange(col.csvHeader, e.target.value)}
                            sx={{ ...selectSx, fontSize: '0.813rem' }}
                          >
                            {MAPPING_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {duplicateMappings.has(col.csvHeader) && (
                          <Tooltip title="Same field mapped twice">
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.25,
                              px: 0.75, py: 0.25, borderRadius: '6px',
                              backgroundColor: (theme.warning || '#D97706') + '20',
                              color: theme.warning || '#D97706',
                              fontSize: '0.65rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
                            }}>
                              <ExclamationTriangleIcon style={{ width: 12, height: 12 }} />
                              Duplicate
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Custom Label Inline Input */}
      {customLabelFor && (
        <Paper sx={{ ...cardSx, borderColor: theme.primary, borderWidth: 2 }} elevation={0}>
          <Typography variant="body2" sx={{ color: theme.text, fontWeight: 600, mb: 1, fontSize: '0.875rem' }}>
            Custom label for &ldquo;{customLabelFor}&rdquo;
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              autoFocus
              placeholder="e.g. Bank Name, Branch, Cheque No..."
              value={customLabelText}
              onChange={(e) => setCustomLabelText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomLabelSave(); }}
              fullWidth
              sx={inputSx}
              inputProps={{ style: { fontSize: '0.875rem', padding: '8px 12px' } }}
            />
            <button
              onClick={handleCustomLabelSave}
              disabled={!customLabelText.trim()}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                backgroundColor: theme.button?.primary?.bg || theme.primary, color: theme.button?.primary?.text || '#FFFFFF',
                fontWeight: 600, fontSize: '0.813rem', cursor: 'pointer', whiteSpace: 'nowrap',
                opacity: customLabelText.trim() ? 1 : 0.5,
              }}
            >
              Add
            </button>
            <button
              onClick={() => { setCustomLabelFor(null); setCustomLabelText(''); }}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${theme.gray?.[300] || '#D4D1CC'}`, backgroundColor: theme.components?.card?.background || '#FFFFFF',
                color: theme.text, fontWeight: 500, fontSize: '0.813rem', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </Box>
        </Paper>
      )}

      {/* Apply VAT toggle */}
      <Paper sx={cardSx} elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Checkbox
            checked={applyVat}
            onChange={(e) => setApplyVat(e.target.checked)}
            sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
            data-testid="apply-vat-checkbox"
          />
          <Box>
            <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500, fontSize: '0.875rem' }}>
              Calculate VAT for transactions in this file
            </Typography>
            <Typography variant="caption" sx={{ color: theme.gray[500], fontSize: '0.75rem' }}>
              Leave unchecked for non-VAT files (e.g. petty cash, owner draws). Saved with the template.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Save as Template */}
      <Paper sx={cardSx} elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Checkbox
            checked={saveTemplate}
            onChange={(e) => setSaveTemplate(e.target.checked)}
            sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
          />
          <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500, fontSize: '0.875rem' }}>
            Save as template for future imports
          </Typography>
        </Box>
        {saveTemplate && (
          <TextField
            size="small"
            placeholder="Template name (e.g. My HDFC Format)"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            fullWidth
            sx={{ ...inputSx, mt: 1.5 }}
            inputProps={{ style: { fontSize: '0.875rem', padding: '8px 12px' } }}
          />
        )}
      </Paper>

      {/* Validation message */}
      {!canProceedStep2 && (
        <Typography variant="body2" sx={{ color: theme.danger || '#DC2626', fontSize: '0.813rem', fontWeight: 500 }}>
          Map at least a Date column and one Amount column to continue.
        </Typography>
      )}

      {/* Continue button */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <SecondaryButton onClick={() => setActiveStep(0)}>
          Back
        </SecondaryButton>
        <PrimaryButton
          onClick={handlePreview}
          disabled={!canProceedStep2 || previewing}
          loading={previewing}
        >
          {previewing ? 'Previewing...' : 'Continue'}
        </PrimaryButton>
      </Box>
    </Box>
  );

  // --- Render Step 3: Category Review ---

  // Group categories for the autocomplete
  const groupedExpenseCategories = useMemo(() => {
    const groups: Record<string, typeof categories> = {
      'Operations': [],
      'Staff': [],
      'Supplies': [],
      'Non-Operational': [],
      'Other': [],
    };
    const staffIds = new Set<number>();
    const supplyNames = new Set(['Food Supplies', 'Beverages', 'Packaging', 'Cleaning Supplies']);
    const nonOpNames = new Set(['Personal / Not Business', 'Loan Repayment', 'Capital / Owner Drawing', 'Transfer Between Accounts']);

    categories.forEach((cat) => {
      if (cat.isStaffCategory) {
        groups['Staff'].push(cat);
        staffIds.add(cat.id);
      } else if (nonOpNames.has(cat.name)) {
        groups['Non-Operational'].push(cat);
      } else if (supplyNames.has(cat.name)) {
        groups['Supplies'].push(cat);
      } else if (['Rent', 'Utilities', 'Equipment', 'Maintenance & Repairs', 'Marketing', 'Licenses & Permits', 'Insurance', 'Gas/Fuel', 'Transportation', 'Petty Cash'].includes(cat.name)) {
        groups['Operations'].push(cat);
      } else {
        groups['Other'].push(cat);
      }
    });

    // Flatten with group labels — preserve isSystem so the editable select can
    // hide rename/delete on system rows.
    const result: Array<{ id: number; name: string; group: string; isSystem?: boolean }> = [];
    Object.entries(groups).forEach(([group, cats]) => {
      cats.forEach((c) => result.push({ id: c.id, name: c.name, group, isSystem: c.isSystem }));
    });
    return result;
  }, [categories]);

  const incomeOptions = useMemo(() => {
    const salesNames = ['DAILY_SALES', 'CATERING', 'EVENTS', 'TIPS'];
    const fromApi = apiIncomeCategories.map((cat) => ({
      value: cat.name,
      label: cat.displayName || cat.name,
      group: cat.isSystem ? (salesNames.includes(cat.name) ? 'Sales' : 'Other') : 'Custom',
    }));
    const custom = customIncomeCategories
      .filter((c) => !apiIncomeCategories.some((a) => a.name.toLowerCase() === c.toLowerCase()))
      .map((c) => ({ value: c, label: c, group: 'Custom' }));
    if (fromApi.length > 0) {
      return [...fromApi, ...custom];
    }
    // Fallback if API hasn't loaded yet
    const standard = [
      { value: 'DAILY_SALES', label: 'Daily Sales', group: 'Sales' },
      { value: 'CATERING', label: 'Catering', group: 'Sales' },
      { value: 'EVENTS', label: 'Events', group: 'Sales' },
      { value: 'TIPS', label: 'Tips', group: 'Sales' },
      { value: 'RENTAL', label: 'Rental Income', group: 'Other' },
      { value: 'DELIVERY_COMMISSION', label: 'Delivery Commission', group: 'Other' },
      { value: 'OTHER', label: 'Other', group: 'Other' },
    ];
    return [...standard, ...custom];
  }, [apiIncomeCategories, customIncomeCategories]);

  const renderCategoryDropdown = (row: PreviewRow) => {
    const edit = rowEdits[row.rowIndex];

    if (row.direction === 'INCOME') {
      return (
        <TextField
          select
          data-testid={`income-category-select-${row.rowIndex}`}
          size="small"
          value={edit?.incomeCategory || ''}
          onChange={(e) => handleRowIncomeCategoryChange(row.rowIndex, e.target.value)}
          sx={{
            minWidth: { xs: 0, sm: 140 }, maxWidth: '100%', width: '100%',
            '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem', backgroundColor: theme.background || '#FAFAF8' },
            '& .MuiSelect-select': { py: '6px' },
          }}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
        >
          {incomeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{opt.label}</MenuItem>
          ))}
        </TextField>
      );
    }

    // Expense categories — restored to original outlined Select look
    const currentCat = groupedExpenseCategories.find((c) => c.id === (edit?.categoryId || 0));
    return (
      <TextField
        select
        data-testid={`category-select-${row.rowIndex}`}
        size="small"
        value={edit?.categoryId || ''}
        onChange={(e) => handleRowCategoryChange(row.rowIndex, Number(e.target.value))}
        title={currentCat?.name || ''}
        sx={{
          minWidth: { xs: 0, sm: 140 }, maxWidth: '100%', width: '100%',
          '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem', backgroundColor: theme.background || '#FAFAF8' },
          '& .MuiSelect-select': { py: '6px' },
        }}
        SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280 } } } }}
      >
        {(() => {
          const nodes: React.ReactNode[] = [];
          let lastGroup: string | null = null;
          groupedExpenseCategories.forEach((cat) => {
            if (cat.group && cat.group !== lastGroup) {
              nodes.push(
                <ListSubheader
                  key={`row-grp-${cat.group}`}
                  disableSticky
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: theme.gray?.[500] || '#8A8A8A',
                    lineHeight: '28px',
                    backgroundColor: theme.background || '#FAFAF8',
                  }}
                >
                  {cat.group}
                </ListSubheader>,
              );
              lastGroup = cat.group;
            }
            nodes.push(
              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                {cat.name}
              </MenuItem>,
            );
          });
          nodes.push(
            <MenuItem
              key="__manage_row__"
              value="__manage_row__"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCategoryManager('EXPENSE');
              }}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme.primary,
                borderTop: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                mt: 0.5,
                py: 0.75,
              }}
            >
              ⚙ Manage categories…
            </MenuItem>,
          );
          return nodes;
        })()}
      </TextField>
    );
  };

  // Render banner with Link / Reconcile / Skip actions for rows that hit
  // either the soft-match dedupe path or an aggregator alias.
  const renderRowActionBanner = (row: PreviewRow) => {
    const edit = rowEdits[row.rowIndex];
    const action = edit?.action ?? 'IMPORT';
    const candidates = row.softMatchCandidates ?? [];
    const alias = row.aggregatorAliasMatch;
    const showAlready = row.alreadyImported;

    if (!showAlready && candidates.length === 0 && !alias) return null;

    const chipBaseSx = {
      px: 0.75,
      py: 0.25,
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
      backgroundColor: 'transparent',
      color: theme.text,
    };
    const activeChipSx = {
      ...chipBaseSx,
      backgroundColor: theme.primary,
      color: 'var(--text-inverse)',
      borderColor: theme.primary,
    };

    return (
      <Box
        sx={{
          mt: 1,
          p: 1,
          backgroundColor: (theme.warning || '#D97706') + '08',
          border: `1px solid ${(theme.warning || '#D97706') + '40'}`,
          borderRadius: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '100%',
        }}
      >
        {showAlready && (
          <Typography variant="caption" sx={{ color: theme.warning || '#D97706', fontWeight: 700 }}>
            Already imported — will be skipped on confirm.
          </Typography>
        )}
        {alias && alias.suggestedAction === 'ADJUSTMENT' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: theme.text, fontWeight: 600 }}>
              Aggregator refund/chargeback: {alias.channelName}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.gray?.[600] || '#666', fontSize: '0.7rem' }}>
              Bank debited from this aggregator. Book as adjustment, not plain expense.
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(['REFUND', 'CHARGEBACK', 'PROMO', 'OTHER'] as const).map((kind) => {
                const isActive = action === 'ADJUSTMENT' && edit?.adjustmentKind === kind;
                return (
                  <Box
                    key={kind}
                    component="button"
                    onClick={() =>
                      setRowAction(row.rowIndex, 'ADJUSTMENT', {
                        adjustmentChannelId: alias.channelId,
                        adjustmentKind: kind,
                      })
                    }
                    sx={isActive ? activeChipSx : chipBaseSx}
                  >
                    {kind.charAt(0) + kind.slice(1).toLowerCase()}
                  </Box>
                );
              })}
              <Box
                component="button"
                onClick={() => setRowAction(row.rowIndex, 'IMPORT')}
                sx={action === 'IMPORT' ? activeChipSx : chipBaseSx}
              >
                Import as plain expense
              </Box>
              <Box
                component="button"
                onClick={() => setRowAction(row.rowIndex, 'SKIP')}
                sx={action === 'SKIP' ? activeChipSx : chipBaseSx}
              >
                Skip
              </Box>
            </Box>
          </Box>
        )}
        {alias && alias.suggestedAction !== 'ADJUSTMENT' && (() => {
          const cp = candidates[0]?.expectedCommissionPercent ?? null;
          const grossSum = candidates.reduce((s, c) => s + (c.recordAmount ?? 0), 0);
          const expectedCommission = cp != null && grossSum > 0 ? grossSum * (cp / 100) : null;
          const projectedDrift = expectedCommission != null
            ? grossSum - row.amount - expectedCommission
            : null;
          const driftPct = expectedCommission != null && grossSum > 0
            ? Math.abs(projectedDrift ?? 0) / grossSum * 100
            : null;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: theme.text, fontWeight: 600 }}>
                Aggregator match: {alias.channelName}
                {cp != null && ` · expected ${cp.toFixed(0)}% commission`}
              </Typography>
              {expectedCommission != null && (
                <Typography variant="caption" sx={{ color: theme.gray?.[600] || '#666', fontSize: '0.7rem' }}>
                  {candidates.length} pending entr{candidates.length === 1 ? 'y' : 'ies'} totalling {formatCurrency(grossSum)}.
                  Bank {formatCurrency(row.amount)} → expected commission {formatCurrency(expectedCommission)}
                  {projectedDrift != null && (
                    <> · projected drift {projectedDrift < 0 ? '−' : '+'}{formatCurrency(Math.abs(projectedDrift))} ({(driftPct ?? 0).toFixed(1)}%)</>
                  )}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Box
                  component="button"
                  onClick={() =>
                    setRowAction(row.rowIndex, 'RECONCILE', { reconcileChannelId: alias.channelId })
                  }
                  sx={action === 'RECONCILE' ? activeChipSx : chipBaseSx}
                >
                  Reconcile to {alias.channelName}
                </Box>
                <Box
                  component="button"
                  onClick={() => setRowAction(row.rowIndex, 'IMPORT')}
                  sx={action === 'IMPORT' ? activeChipSx : chipBaseSx}
                >
                  Import as income
                </Box>
                <Box
                  component="button"
                  onClick={() => setRowAction(row.rowIndex, 'SKIP')}
                  sx={action === 'SKIP' ? activeChipSx : chipBaseSx}
                >
                  Skip
                </Box>
              </Box>
            </Box>
          );
        })()}
        {candidates.length > 0 && (() => {
          const hasDrift = candidates.some((c) => c.driftKind && c.driftKind !== 'NORMAL');
          const headerLabel = hasDrift
            ? `Possible commission drift — review match${candidates.length > 1 ? 'es' : ''}`
            : `Possible match${candidates.length > 1 ? 'es' : ''}`;
          const driftChipSx = (kind: SoftMatchCandidate['driftKind']) => {
            if (kind === 'COMMISSION_BAND') {
              return {
                px: 0.6,
                py: 0.1,
                borderRadius: '4px',
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: (theme.warning || '#D97706') + '20',
                color: theme.warning || '#D97706',
                border: `1px solid ${theme.warning || '#D97706'}40`,
              };
            }
            if (kind === 'OUT_OF_BAND') {
              return {
                px: 0.6,
                py: 0.1,
                borderRadius: '4px',
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: (theme.danger || '#DC2626') + '15',
                color: theme.danger || '#DC2626',
                border: `1px solid ${theme.danger || '#DC2626'}40`,
              };
            }
            return null;
          };
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: theme.text, fontWeight: 600 }}>
                {headerLabel}:
              </Typography>
              {candidates.map((c) => {
                const dKindSx = driftChipSx(c.driftKind);
                const driftLabel =
                  c.driftAmount !== undefined && c.driftAmount !== null && c.driftPercent != null
                    ? `${c.driftAmount < 0 ? '−' : '+'}${formatCurrency(Math.abs(c.driftAmount))} (${c.driftPercent.toFixed(1)}%)`
                    : null;
                const isLinked =
                  action === 'LINK'
                  && (edit?.linkedExpenseId === c.recordId || edit?.linkedIncomeId === c.recordId);
                return (
                  <Box
                    key={`${c.recordType}-${c.recordId}`}
                    sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}
                  >
                    <Typography variant="caption" sx={{ color: theme.gray?.[600] || '#666' }}>
                      {c.recordSupplier} · {c.recordDate} · {formatCurrency(c.recordAmount)}
                      {c.driftKind === 'NORMAL' && ` (${Math.round(c.similarity * 100)}% match)`}
                    </Typography>
                    {dKindSx && driftLabel && (
                      <Box component="span" sx={dKindSx}>
                        {driftLabel}
                        {c.driftKind === 'COMMISSION_BAND' && c.expectedCommissionPercent != null
                          && ` ≈ ${c.channelName} ${c.expectedCommissionPercent.toFixed(0)}%`}
                      </Box>
                    )}
                    <Box
                      component="button"
                      onClick={() => {
                        const isCommissionLink =
                          c.driftKind === 'COMMISSION_BAND'
                          && c.recordType === 'INCOME'
                          && c.driftAmount != null
                          && c.channelId != null;
                        setRowAction(row.rowIndex, 'LINK', {
                          linkedExpenseId: c.recordType === 'EXPENSE' ? c.recordId : undefined,
                          linkedIncomeId: c.recordType === 'INCOME' ? c.recordId : undefined,
                          bookCommissionExpense: isCommissionLink || undefined,
                          commissionAmount: isCommissionLink
                            ? Math.abs(c.driftAmount as number)
                            : undefined,
                          commissionChannelId: isCommissionLink ? (c.channelId as number) : undefined,
                        });
                      }}
                      sx={isLinked ? activeChipSx : chipBaseSx}
                    >
                      {c.driftKind === 'COMMISSION_BAND' ? 'Link (book commission)' : 'Link'}
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Box
                  component="button"
                  onClick={() => setRowAction(row.rowIndex, 'IMPORT')}
                  sx={action === 'IMPORT' ? activeChipSx : chipBaseSx}
                >
                  Import as new
                </Box>
                <Box
                  component="button"
                  onClick={() => setRowAction(row.rowIndex, 'SKIP')}
                  sx={action === 'SKIP' ? activeChipSx : chipBaseSx}
                >
                  Skip
                </Box>
              </Box>
            </Box>
          );
        })()}
      </Box>
    );
  };

  // Find first uncategorized row index for the ref
  const firstUncategorizedIdx = useMemo(() => {
    return filteredRows.find((r) => {
      const edit = rowEdits[r.rowIndex];
      if (edit?.skip) return false;
      if (r.direction === 'EXPENSE') return !edit?.categoryId;
      return !edit?.incomeCategory;
    })?.rowIndex ?? null;
  }, [filteredRows, rowEdits]);

  const renderStep3 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Overall summary card */}
      <Paper
        sx={{
          ...cardSx,
          py: 1.5,
          display: 'flex',
          gap: { xs: 0.75, sm: 2 },
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        elevation={0}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.danger || '#DC2626', fontSize: '0.813rem' }}>
          {expenseCount} Expenses ({formatCurrency(overallSummary.expenseTotal)})
        </Typography>
        <Typography variant="body2" sx={{ color: theme.gray?.[400] || '#8A8A8A', fontSize: '0.813rem' }}>&bull;</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.success || '#16A34A', fontSize: '0.813rem' }}>
          {incomeCount} Income ({formatCurrency(overallSummary.incomeTotal)})
        </Typography>
        <Typography variant="body2" sx={{ color: theme.gray?.[400] || '#8A8A8A', fontSize: '0.813rem' }}>&bull;</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.813rem' }}>
          {overallSummary.skipped} Skipped
        </Typography>
        {summaryStats.uncategorized > 0 && (
          <>
            <Typography variant="body2" sx={{ color: theme.gray?.[400] || '#8A8A8A', fontSize: '0.813rem' }}>&bull;</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.danger || '#DC2626', fontSize: '0.813rem' }}>
              {summaryStats.uncategorized} uncategorized
            </Typography>
          </>
        )}
      </Paper>

      {/* Tab chips */}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        {[
          { key: 'EXPENSE' as const, label: `Expenses (${expenseCount})` },
          { key: 'INCOME' as const, label: `Income (${incomeCount})` },
        ].map((tab) => (
          <Chip
            key={tab.key}
            label={tab.label}
            onClick={() => setReviewTab(tab.key)}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 32,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: reviewTab === tab.key ? `${theme.primary}22` : (theme.gray?.[100] || '#F0EDE8'),
              color: reviewTab === tab.key ? theme.primary : (theme.gray?.[500] || '#6B6B6B'),
              border: reviewTab === tab.key ? `1.5px solid ${theme.primary}` : '1px solid transparent',
            }}
          />
        ))}
      </Box>

      {/* Bulk actions toolbar */}
      {selectedRows.size > 0 && (
        <Paper
          sx={{
            ...cardSx,
            py: 1.5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            backgroundColor: `${theme.primary}08`,
            borderColor: `${theme.primary}40`,
          }}
          elevation={0}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.813rem' }}>
            {selectedRows.size} selected
          </Typography>
          <FormControl size="small" sx={{ ...inputSx, minWidth: 160 }}>
            <InputLabel>Set Category</InputLabel>
            <Select
              value=""
              label="Set Category"
              onChange={(e) => handleBulkCategory(e.target.value as number)}
              sx={{ ...selectSx, fontSize: '0.75rem' }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <SecondaryButton
            size="small"
            onClick={handleBulkSkip}
            sx={{ fontSize: '0.75rem' }}
          >
            Skip Selected
          </SecondaryButton>
          <SecondaryButton
            size="small"
            onClick={() => setSelectedRows(new Set())}
            sx={{ fontSize: '0.75rem' }}
          >
            Uncheck All
          </SecondaryButton>
        </Paper>
      )}

      {/* Inline add category */}
      {addingCategoryInline && (
        <Paper sx={{ ...cardSx, display: 'flex', gap: 1, alignItems: 'center' }} elevation={0}>
          <TextField
            size="small"
            placeholder="New category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            autoFocus
            sx={{ ...inputSx, flex: 1 }}
            inputProps={{ style: { fontSize: '0.875rem', padding: '6px 12px' } }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategoryInline(); }}
          />
          <PrimaryButton size="small" onClick={handleAddCategoryInline} disabled={!newCategoryName.trim()} sx={{ minHeight: 36 }}>
            Add
          </PrimaryButton>
          <AppIconButton
            icon={<XMarkIcon style={{ width: 18, height: 18 }} />}
            tooltip="Cancel"
            variant="ghost"
            size="small"
            onClick={() => { setAddingCategoryInline(false); setNewCategoryName(''); }}
          />
        </Paper>
      )}

      {/* Inline add income category */}
      {addingIncomeCategoryInline && (
        <Paper sx={{ ...cardSx, display: 'flex', gap: 1, alignItems: 'center' }} elevation={0}>
          <TextField
            size="small"
            placeholder="New income category name"
            value={newIncomeCategoryName}
            onChange={(e) => setNewIncomeCategoryName(e.target.value)}
            autoFocus
            sx={{ ...inputSx, flex: 1 }}
            inputProps={{ style: { fontSize: '0.875rem', padding: '6px 12px' } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newIncomeCategoryName.trim()) {
                const name = newIncomeCategoryName.trim();
                setCustomIncomeCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
                toast.success(`Income category "${name}" added`);
                setNewIncomeCategoryName('');
                setAddingIncomeCategoryInline(false);
              }
            }}
          />
          <PrimaryButton
            size="small"
            disabled={!newIncomeCategoryName.trim()}
            onClick={() => {
              const name = newIncomeCategoryName.trim();
              if (!name) return;
              setCustomIncomeCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
              toast.success(`Income category "${name}" added`);
              setNewIncomeCategoryName('');
              setAddingIncomeCategoryInline(false);
            }}
            sx={{ minHeight: 36 }}
          >
            Add
          </PrimaryButton>
          <AppIconButton
            icon={<XMarkIcon style={{ width: 18, height: 18 }} />}
            tooltip="Cancel"
            variant="ghost"
            size="small"
            onClick={() => { setAddingIncomeCategoryInline(false); setNewIncomeCategoryName(''); }}
          />
        </Paper>
      )}

      {/* Rows */}
      {isMobile ? (
        // Mobile: card layout
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {paginatedRows.map((row) => {
            const edit = rowEdits[row.rowIndex];
            const isUncategorized = row.direction === 'EXPENSE' ? !edit?.categoryId : !edit?.incomeCategory;
            const isFirstUncategorized = row.rowIndex === firstUncategorizedIdx;
            return (
              <Paper
                key={row.rowIndex}
                data-testid={`preview-row-${row.rowIndex}`}
                ref={isFirstUncategorized ? uncategorizedRef : undefined}
                sx={{
                  ...cardSx,
                  opacity: edit?.skip ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                  borderColor: highlightedRows.has(row.rowIndex)
                    ? theme.primary
                    : isUncategorized && !edit?.skip
                      ? (theme.danger || '#D99982') + '60'
                      : theme.components?.card?.border || '#E0E0E0',
                  backgroundColor: highlightedRows.has(row.rowIndex)
                    ? theme.button.primary.bg
                    : cardSx.backgroundColor,
                }}
                elevation={0}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selectedRows.has(row.rowIndex)}
                      onChange={() => handleSelectRow(row.rowIndex)}
                      sx={{ p: 0.25, color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
                    />
                    {editingDescRow === row.rowIndex ? (
                      <TextField
                        size="small"
                        value={edit?.description || row.description}
                        onChange={(e) => handleRowDescriptionEdit(row.rowIndex, e.target.value)}
                        onBlur={() => setEditingDescRow(null)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingDescRow(null); }}
                        autoFocus
                        sx={{ ...inputSx }}
                        inputProps={{ style: { fontSize: '0.813rem', padding: '4px 8px' } }}
                      />
                    ) : (
                      <Typography
                        onClick={() => setEditingDescRow(row.rowIndex)}
                        sx={{
                          fontWeight: 600,
                          color: theme.text,
                          fontSize: '0.813rem',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 200,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {edit?.description || row.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: row.direction === 'EXPENSE' ? (theme.danger || '#DC2626') : (theme.success || '#16A34A'), fontSize: '0.95rem' }}>
                    {formatCurrency(row.amount)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B' }}>{row.date}</Typography>
                  {row.bankCategory && (
                    <Box sx={{ px: 0.75, py: 0.125, borderRadius: '4px', backgroundColor: theme.gray?.[100] || '#F0EDE8', fontSize: '0.65rem', fontWeight: 500, color: theme.gray?.[600] || '#8A8A8A' }}>
                      {row.bankCategory}
                    </Box>
                  )}
                  <ConfidenceBadge confidence={edit?.categoryId || edit?.incomeCategory ? 'HIGH' : row.confidence} autoApplied={edit?.autoApplied} rowIndex={row.rowIndex} />
                  {row.possibleDuplicate && (
                    <Tooltip title="A similar transaction may already exist">
                      <Box sx={{ px: 0.75, py: 0.125, borderRadius: '4px', backgroundColor: (theme.warning || '#D97706') + '20', color: theme.warning || '#D97706', fontSize: '0.65rem', fontWeight: 700 }}>
                        Possible duplicate
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {/* Category dropdown */}
                <Box sx={{ mb: 1.5 }}>
                  {renderCategoryDropdown(row)}
                </Box>

                {/* Cross-source dedupe + aggregator banner */}
                {renderRowActionBanner(row)}

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      data-testid={`skip-row-${row.rowIndex}`}
                      size="small"
                      checked={edit?.skip || false}
                      onChange={() => handleRowSkipToggle(row.rowIndex)}
                      sx={{ p: 0.25, color: theme.gray?.[400], '&.Mui-checked': { color: theme.gray?.[500] } }}
                    />
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.7rem' }}>Skip</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Switch
                      data-testid={`save-rule-${row.rowIndex}`}
                      size="small"
                      checked={edit?.saveAsRule || false}
                      onChange={() => handleRowRuleToggle(row.rowIndex)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.7rem' }}>Save Rule</Typography>
                  </Box>
                </Box>
              </Paper>
            );
          })}
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={reviewPage}
            onPageChange={(_e, newPage) => setReviewPage(newPage)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            sx={{
              '& .MuiTablePagination-toolbar': { minHeight: 40 },
              '& .MuiTablePagination-displayedRows': { fontSize: '0.75rem', color: theme.gray?.[500] || '#6B6B6B' },
            }}
          />
        </Box>
      ) : (
        // Desktop: paginated table layout
        <Paper sx={cardSx} elevation={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedRows.size === filteredRows.length && filteredRows.length > 0}
                      indeterminate={selectedRows.size > 0 && selectedRows.size < filteredRows.length}
                      onChange={() => {
                        if (selectedRows.size === filteredRows.length) setSelectedRows(new Set());
                        else setSelectedRows(new Set(filteredRows.map((r) => r.rowIndex)));
                      }}
                      sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }}>Bank Cat</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem', minWidth: 180 }}>SOG Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }}>Confidence</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }}>
                    <Tooltip title="Check if this transaction is a duplicate of an existing expense. Checked rows will be skipped on import." arrow>
                      <span style={{ cursor: 'help', borderBottom: `1px dotted ${theme.gray?.[400] || '#8A8A8A'}` }}>Dup?</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.text, fontSize: '0.7rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => {
                  const edit = rowEdits[row.rowIndex];
                  const isUncategorized = row.direction === 'EXPENSE' ? !edit?.categoryId : !edit?.incomeCategory;
                  const isFirstUncategorized = row.rowIndex === firstUncategorizedIdx;
                  return (
                    <TableRow
                      key={row.rowIndex}
                      data-testid={`preview-row-${row.rowIndex}`}
                      ref={isFirstUncategorized ? uncategorizedRef : undefined}
                      sx={{
                        opacity: edit?.skip ? 0.45 : 1,
                        backgroundColor: highlightedRows.has(row.rowIndex)
                          ? `${theme.primary}14`
                          : isUncategorized && !edit?.skip ? (theme.danger || '#D99982') + '06' : 'transparent',
                        transition: 'all 0.3s ease',
                        '&:hover': { backgroundColor: `${theme.primary}04` },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedRows.has(row.rowIndex)}
                          onChange={() => handleSelectRow(row.rowIndex)}
                          sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {editingDescRow === row.rowIndex ? (
                          <TextField
                            size="small"
                            value={edit?.description || row.description}
                            onChange={(e) => handleRowDescriptionEdit(row.rowIndex, e.target.value)}
                            onBlur={() => setEditingDescRow(null)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setEditingDescRow(null); }}
                            autoFocus
                            sx={{ ...inputSx }}
                            inputProps={{ style: { fontSize: '0.75rem', padding: '4px 8px' } }}
                          />
                        ) : (
                          <Typography
                            onClick={() => setEditingDescRow(row.rowIndex)}
                            sx={{
                              fontWeight: 500,
                              color: theme.text,
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {edit?.description || row.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700, color: row.direction === 'EXPENSE' ? (theme.danger || '#DC2626') : (theme.success || '#16A34A'), fontSize: '0.813rem' }}>
                          {formatCurrency(row.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.bankCategory ? (
                          <Box sx={{ px: 0.75, py: 0.25, borderRadius: '4px', backgroundColor: theme.gray?.[100] || '#F0EDE8', fontSize: '0.65rem', fontWeight: 500, color: theme.gray?.[600] || '#8A8A8A', display: 'inline-block' }}>
                            {row.bankCategory}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#8A8A8A' }}>--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {renderCategoryDropdown(row)}
                      </TableCell>
                      <TableCell>
                        <ConfidenceBadge confidence={edit?.categoryId || edit?.incomeCategory ? 'HIGH' : row.confidence} autoApplied={edit?.autoApplied} rowIndex={row.rowIndex} />
                      </TableCell>
                      <TableCell align="center">
                        {row.possibleDuplicate ? (
                          <Tooltip title="A similar transaction may already exist. This row will be skipped on import.">
                            <Box sx={{ px: 0.5, py: 0.125, borderRadius: '4px', backgroundColor: (theme.warning || '#D97706') + '20', color: theme.warning || '#D97706', fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-block' }}>
                              Yes
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#8A8A8A' }}>--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Skip this row">
                              <Checkbox
                                data-testid={`skip-row-${row.rowIndex}`}
                                size="small"
                                checked={edit?.skip || false}
                                onChange={() => handleRowSkipToggle(row.rowIndex)}
                                sx={{ p: 0.25, color: theme.gray?.[400], '&.Mui-checked': { color: theme.gray?.[500] } }}
                              />
                            </Tooltip>
                            <Tooltip title="Save category as rule for future imports">
                              <Switch
                                data-testid={`save-rule-${row.rowIndex}`}
                                size="small"
                                checked={edit?.saveAsRule || false}
                                onChange={() => handleRowRuleToggle(row.rowIndex)}
                                sx={{
                                  transform: 'scale(0.8)',
                                  '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary },
                                }}
                              />
                            </Tooltip>
                          </Box>
                          {renderRowActionBanner(row)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={reviewPage}
            onPageChange={(_e, newPage) => setReviewPage(newPage)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            sx={{
              '& .MuiTablePagination-toolbar': { minHeight: 40 },
              '& .MuiTablePagination-displayedRows': { fontSize: '0.75rem', color: theme.gray?.[500] || '#6B6B6B' },
              borderTop: `1px solid ${theme.gray?.[200] || '#E8E6E1'}`,
            }}
          />
        </Paper>
      )}

      {/* Sticky footer bar with Back + Import button */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          backgroundColor: theme.background || '#FAFAF8',
          borderTop: `1px solid ${theme.gray?.[200] || '#E8E6E1'}`,
          py: 1.5,
          px: { xs: 1, sm: 2 },
          mx: { xs: -1.5, sm: -3 },
          mb: { xs: -12, sm: -4 },
          display: 'flex',
          gap: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <SecondaryButton onClick={() => setActiveStep(1)}>
          Back
        </SecondaryButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {totalUncategorized > 0 && (
            <Typography variant="body2" sx={{ color: theme.danger || '#DC2626', fontSize: '0.75rem', fontWeight: 600 }}>
              {totalUncategorized} uncategorized
            </Typography>
          )}
          <PrimaryButton
            onClick={() => setActiveStep(3)}
            disabled={totalUncategorized > 0}
          >
            Import {previewData ? previewData.rows.filter((r) => !rowEdits[r.rowIndex]?.skip).length : 0} Transactions
          </PrimaryButton>
        </Box>
      </Box>

      {/* Scroll-to-uncategorized FAB */}
      {totalUncategorized > 0 && (
        <Fab
          onClick={scrollToUncategorized}
          size="small"
          sx={{
            position: 'fixed',
            bottom: { xs: 80, sm: 24 },
            right: 24,
            backgroundColor: (theme.danger || '#DC2626') + '20',
            color: theme.danger || '#DC2626',
            border: `1.5px solid ${theme.danger || '#DC2626'}`,
            boxShadow: 'none',
            '&:hover': { backgroundColor: (theme.danger || '#DC2626') + '30' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
            <ArrowDownIcon style={{ width: 16, height: 16 }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>{totalUncategorized}</Typography>
          </Box>
        </Fab>
      )}
    </Box>
  );

  // --- Render Step 4: Confirm & Result ---

  const renderStep4 = () => {
    if (!importResult) {
      // Confirmation dialog
      const skipCount = previewData?.rows.filter((r) => rowEdits[r.rowIndex]?.skip).length || 0;
      const expCount = previewData?.rows.filter((r) => r.direction === 'EXPENSE' && !rowEdits[r.rowIndex]?.skip).length || 0;
      const incCount = previewData?.rows.filter((r) => r.direction === 'INCOME' && !rowEdits[r.rowIndex]?.skip).length || 0;

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, alignItems: 'center', py: { xs: 2, sm: 4 } }}>
          <Paper sx={{ ...cardSx, textAlign: 'center', maxWidth: 480, width: '100%' }} elevation={0}>
            <ExclamationTriangleIcon style={{ width: 40, height: 40, color: theme.warning || '#D97706', margin: '0 auto 12px' }} />
            <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.1rem', mb: 1 }}>
              Confirm Import
            </Typography>
            <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.875rem', mb: 3 }}>
              Create {expCount} expenses and {incCount} income entries?
              {skipCount > 0 && <> Skip {skipCount}?</>}
            </Typography>

            {importing && (
              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={importProgress}
                  sx={{
                    height: 8,
                    borderRadius: '4px',
                    backgroundColor: theme.gray?.[200] || '#E8E6E1',
                    '& .MuiLinearProgress-bar': { backgroundColor: theme.primary, borderRadius: '4px' },
                  }}
                />
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B', mt: 0.5, display: 'block' }}>
                  Importing...
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <SecondaryButton onClick={() => setActiveStep(2)} disabled={importing}>
                Back
              </SecondaryButton>
              <PrimaryButton
                data-testid="confirm-import-btn"
                onClick={handleConfirmImport}
                disabled={importing}
                loading={importing}
                startIcon={!importing ? <CheckCircleIcon style={{ width: 20, height: 20 }} /> : undefined}
              >
                {importing ? 'Importing...' : 'Confirm Import'}
              </PrimaryButton>
            </Box>
          </Paper>
        </Box>
      );
    }

    // Result card
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, alignItems: 'center', py: { xs: 2, sm: 4 } }}>
        <Paper data-testid="import-result" sx={{ ...cardSx, textAlign: 'center', maxWidth: 480, width: '100%' }} elevation={0}>
          <CheckCircleIcon style={{ width: 56, height: 56, color: theme.success || '#16A34A', margin: '0 auto 16px' }} />
          <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.25rem', mb: 0.5 }}>
            Import Complete
          </Typography>
          <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.875rem', mb: 3 }}>
            Your bank statement has been processed successfully.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
              mb: 3,
              px: { xs: 1, sm: 2 },
            }}
          >
            {[
              { label: 'Expenses Created', value: importResult.expensesCreated, color: theme.danger || '#DC2626' },
              { label: 'Income Created', value: importResult.incomeCreated, color: theme.success || '#16A34A' },
              { label: 'Linked to Existing', value: importResult.linkedRecords ?? 0, color: theme.warning || '#D97706' },
              { label: 'Reconciled to Settlement', value: importResult.reconciledRows ?? 0, color: theme.primary },
              { label: 'Already Imported (skipped)', value: importResult.alreadyImportedSkipped ?? 0, color: theme.gray?.[500] || '#6B6B6B' },
              { label: 'User Skipped', value: importResult.skipped, color: theme.gray?.[400] || '#8A8A8A' },
              { label: 'Rules Learned', value: importResult.rulesLearned, color: theme.primary },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  backgroundColor: stat.color + '08',
                  border: `1px solid ${stat.color}20`,
                }}
              >
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, color: stat.color, fontSize: '1.25rem', lineHeight: 1.2, mt: 0.25 }}>
                  {stat.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {(importResult.reconciledRows ?? 0) > 0 && (
            <Box
              sx={{
                p: 1.25,
                mb: 2,
                borderRadius: '8px',
                backgroundColor: (theme.primary || '#688268') + '10',
                border: `1px solid ${(theme.primary || '#688268')}30`,
                textAlign: 'left',
              }}
            >
              <Typography variant="caption" sx={{ color: theme.text, fontWeight: 600, display: 'block' }}>
                {importResult.reconciledRows} aggregator payout{importResult.reconciledRows === 1 ? '' : 's'} created settlement records.
              </Typography>
              <Typography variant="caption" sx={{ color: theme.gray?.[600] || '#666', display: 'block', fontSize: '0.7rem' }}>
                Commission drift, allocations, and unexplained variance are on the Aggregators page.
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <SecondaryButton
              onClick={() => navigate('/vendor/finance/expenses')}
            >
              Go to Expenses
            </SecondaryButton>
            <SecondaryButton
              onClick={() => navigate('/vendor/finance/income')}
            >
              Go to Income
            </SecondaryButton>
            {(importResult.reconciledRows ?? 0) > 0 && (
              <SecondaryButton
                onClick={() => navigate('/vendor/finance/aggregators')}
              >
                Go to Aggregators
              </SecondaryButton>
            )}
            <PrimaryButton onClick={handleReset}>
              Import Another
            </PrimaryButton>
          </Box>
        </Paper>
      </Box>
    );
  };

  // --- Main Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 4 } }}>
      <div data-testid="import-toast" />
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand eyebrow="Finance · Bank Import" title="Import Bank Statement" />

      {/* Stepper */}
      <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'horizontal' : 'horizontal'}
          sx={{
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.65rem', sm: '0.813rem' },
              fontWeight: 600,
              color: theme.gray?.[400] || '#8A8A8A',
              '&.Mui-active': { color: theme.primary },
              '&.Mui-completed': { color: theme.success || '#16A34A' },
            },
            '& .MuiStepIcon-root': {
              color: theme.gray?.[300] || '#D4D1CC',
              '&.Mui-active': { color: theme.primary },
              '&.Mui-completed': { color: theme.success || '#16A34A' },
            },
            '& .MuiStepConnector-line': {
              borderColor: theme.gray?.[300] || '#D4D1CC',
            },
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step Content */}
      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}
      {activeStep === 3 && renderStep4()}

      {/* Transaction Rules Modal */}
      {showRulesModal && (
        <Modal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} title="Transaction Rules" maxWidth="md">
          <TransactionRulesManager />
        </Modal>
      )}

      {/* Re-import warning dialog */}
      <ConfirmDialog
        open={showReimportDialog}
        title="File Already Imported"
        message={
          detectResult?.alreadyImported && typeof detectResult.alreadyImported === 'object'
            ? `This file was imported on ${new Date(detectResult.alreadyImported.importedAt).toLocaleDateString()}. ${detectResult.alreadyImported.expensesCreated} expenses, ${detectResult.alreadyImported.incomeCreated} income entries were created. Re-importing will add new entries without removing previous ones.`
            : 'This file was already imported. Re-importing will add new entries without removing previous ones.'
        }
        confirmLabel="Re-import Anyway"
        cancelLabel="Cancel"
        variant="confirm"
        onConfirm={handleReimportAnyway}
        onCancel={handleReimportCancel}
      />

      {/* Category Mapping Wizard Modal */}
      {showMappingWizard && (
        <Modal
          isOpen={showMappingWizard}
          onClose={handleWizardSkip}
          title="Category Mapping Wizard"
          subtitle="Assign categories to unique vendors/descriptions — applies to all matching transactions"
          maxWidth="lg"
          footer={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1.5, flexWrap: 'wrap' }}>
              <SecondaryButton onClick={handleWizardSkip}>
                Skip — categorize later
              </SecondaryButton>
              <PrimaryButton onClick={handleWizardApply}>
                Apply & Continue
              </PrimaryButton>
            </Box>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Summary bar */}
            <Paper
              sx={{
                ...cardSx,
                py: 1.5,
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
              elevation={0}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text, fontSize: '0.813rem' }}>
                {wizardGroups.length} unique pattern{wizardGroups.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray?.[400], fontSize: '0.813rem' }}>&bull;</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: theme.danger || '#DC2626', fontSize: '0.813rem' }}>
                {wizardGroups.filter((g) => g.isUncategorized).length} need attention
              </Typography>
              <Typography variant="body2" sx={{ color: theme.gray?.[400], fontSize: '0.813rem' }}>&bull;</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.success || '#16A34A', fontSize: '0.813rem' }}>
                {wizardGroups.filter((g) => !g.isUncategorized).length} already mapped
              </Typography>
            </Paper>

            {/* Scrollable list of pattern groups */}
            <Box
              sx={{
                maxHeight: { xs: '55vh', sm: '60vh' },
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                pr: 0.5,
              }}
            >
              {wizardGroups.map((group) => {
                const key = `${group.pattern.toUpperCase()}__${group.direction}`;
                const override = wizardOverrides[key];

                // Determine the effective category (override > rowEdit > suggested)
                const effectiveCategoryId = override?.categoryId ?? group.currentCategoryId;
                const effectiveIncomeCategory = override?.incomeCategory ?? group.currentIncomeCategory;
                const isSaveAsRule = override?.saveAsRule ?? false;

                // Check if still uncategorized after overrides
                const stillUncategorized = group.direction === 'EXPENSE'
                  ? !effectiveCategoryId
                  : !effectiveIncomeCategory;

                return (
                  <Paper
                    key={key}
                    sx={{
                      ...cardSx,
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 1.5, sm: 2 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 2 },
                      flexWrap: 'wrap',
                      borderColor: stillUncategorized
                        ? `${theme.danger || '#DC2626'}40`
                        : (theme.components?.card?.border || '#E8E6E1'),
                      backgroundColor: stillUncategorized
                        ? `${theme.danger || '#DC2626'}04`
                        : (theme.components?.card?.background || '#FFFFFF'),
                      transition: 'all 0.2s ease',
                    }}
                    elevation={0}
                  >
                    {/* Pattern name + direction badge */}
                    <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: theme.text,
                            fontSize: { xs: '0.75rem', sm: '0.813rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={group.pattern}
                        >
                          {group.pattern}
                        </Typography>
                        <Chip
                          label={group.direction === 'EXPENSE' ? 'Expense' : 'Income'}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            backgroundColor: group.direction === 'EXPENSE'
                              ? `${theme.danger || '#DC2626'}15`
                              : `${theme.success || '#16A34A'}15`,
                            color: group.direction === 'EXPENSE'
                              ? (theme.danger || '#DC2626')
                              : (theme.success || '#16A34A'),
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#6B6B6B', fontSize: '0.7rem' }}>
                        {group.rows.length} transaction{group.rows.length !== 1 ? 's' : ''} &middot; {formatCurrency(group.totalAmount)}
                      </Typography>
                    </Box>

                    {/* Category dropdown — restored to original outlined Select look */}
                    <Box sx={{ flex: '0 0 auto', minWidth: { xs: '100%', sm: 180 }, maxWidth: { xs: '100%', sm: 220 } }}>
                      {group.direction === 'EXPENSE' ? (
                        <TextField
                          select
                          size="small"
                          value={effectiveCategoryId ?? ''}
                          onChange={(e) => handleWizardCategoryChange(group, Number(e.target.value))}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              backgroundColor: theme.background || '#FAFAF8',
                            },
                            '& .MuiSelect-select': { py: '6px' },
                          }}
                          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280, fontSize: '0.8rem' } } } }}
                        >
                          {(() => {
                            // Render group headers between categories so the user
                            // sees Operations / Staff / Supplies / etc. as section
                            // labels in the dropdown.
                            const nodes: React.ReactNode[] = [];
                            let lastGroup: string | null = null;
                            groupedExpenseCategories.forEach((cat) => {
                              if (cat.group && cat.group !== lastGroup) {
                                nodes.push(
                                  <ListSubheader
                                    key={`grp-${cat.group}`}
                                    disableSticky
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                      color: theme.gray?.[500] || '#8A8A8A',
                                      lineHeight: '28px',
                                      backgroundColor: theme.background || '#FAFAF8',
                                    }}
                                  >
                                    {cat.group}
                                  </ListSubheader>,
                                );
                                lastGroup = cat.group;
                              }
                              nodes.push(
                                <MenuItem
                                  key={cat.id}
                                  value={cat.id}
                                  sx={{ fontSize: '0.8rem', py: 0.5 }}
                                >
                                  {cat.name}
                                </MenuItem>,
                              );
                            });
                            // Footer link to open the manage-categories drawer.
                            nodes.push(
                              <MenuItem
                                key="__manage__"
                                value="__manage__"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setCategoryManager('EXPENSE');
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: theme.primary,
                                  borderTop: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                                  mt: 0.5,
                                  py: 0.75,
                                }}
                              >
                                ⚙ Manage categories…
                              </MenuItem>,
                            );
                            return nodes;
                          })()}
                        </TextField>
                      ) : (
                        <TextField
                          select
                          size="small"
                          value={effectiveIncomeCategory ?? ''}
                          onChange={(e) => handleWizardIncomeCategoryChange(group, e.target.value)}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              backgroundColor: theme.background || '#FAFAF8',
                            },
                            '& .MuiSelect-select': { py: '6px' },
                          }}
                          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 280, fontSize: '0.8rem' } } } }}
                        >
                          {(() => {
                            const nodes: React.ReactNode[] = [];
                            let lastGroup: string | null = null;
                            incomeOptions.forEach((opt) => {
                              if (opt.group && opt.group !== lastGroup) {
                                nodes.push(
                                  <ListSubheader
                                    key={`inc-grp-${opt.group}`}
                                    disableSticky
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.05em',
                                      color: theme.gray?.[500] || '#8A8A8A',
                                      lineHeight: '28px',
                                      backgroundColor: theme.background || '#FAFAF8',
                                    }}
                                  >
                                    {opt.group}
                                  </ListSubheader>,
                                );
                                lastGroup = opt.group;
                              }
                              nodes.push(
                                <MenuItem
                                  key={opt.value}
                                  value={opt.value}
                                  sx={{ fontSize: '0.8rem', py: 0.5 }}
                                >
                                  {opt.label}
                                </MenuItem>,
                              );
                            });
                            nodes.push(
                              <MenuItem
                                key="__manage_income__"
                                value="__manage_income__"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setCategoryManager('INCOME');
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: theme.primary,
                                  borderTop: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                                  mt: 0.5,
                                  py: 0.75,
                                }}
                              >
                                ⚙ Manage categories…
                              </MenuItem>,
                            );
                            return nodes;
                          })()}
                        </TextField>
                      )}
                    </Box>

                    {/* Save as rule checkbox */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSaveAsRule}
                          onChange={() => handleWizardRuleToggle(group)}
                          size="small"
                          sx={{
                            color: theme.gray?.[400] || '#8A8A8A',
                            '&.Mui-checked': { color: theme.primary },
                            p: 0.5,
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.7rem', color: theme.gray?.[500] || '#6B6B6B', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Save rule
                        </Typography>
                      }
                      sx={{ m: 0, ml: { xs: 0, sm: 0.5 } }}
                    />
                  </Paper>
                );
              })}

              {wizardGroups.length === 0 && (
                <EmptyState
                  icon={<CheckCircleIcon />}
                  title="Nothing left to categorize"
                  subtitle="Every transaction in this statement already has a category — close this window to continue."
                />
              )}
            </Box>
          </Box>
        </Modal>
      )}

      {/* Manage Categories drawer — opened from inside category dropdowns. */}
      <CategoryManagerDrawer
        open={categoryManager === 'EXPENSE'}
        onClose={() => setCategoryManager(null)}
        title="Manage Expense Categories"
        groups={['Operations', 'Staff', 'Supplies', 'Non-Operational', 'Other']}
        items={groupedExpenseCategories.map((c) => ({
          id: c.id,
          name: c.name,
          group: c.group,
          isSystem: c.isSystem === true,
        }))}
        onCreate={async (name) => {
          await handleCreateExpenseCategory(name);
        }}
        onRename={async (id, name) => {
          await handleRenameExpenseCategory(Number(id), name);
        }}
        onDelete={async (id) => {
          await handleDeleteExpenseCategory(Number(id));
        }}
      />
      <CategoryManagerDrawer
        open={categoryManager === 'INCOME'}
        onClose={() => setCategoryManager(null)}
        title="Manage Income Categories"
        groups={['Sales', 'Other', 'Custom']}
        items={incomeOptions.map((opt) => {
          const apiCat = apiIncomeCategories.find((c) => c.name === opt.value);
          return {
            id: apiCat ? apiCat.id : opt.value,
            name: opt.label,
            group: opt.group,
            isSystem: apiCat?.isSystem === true,
          };
        })}
        onCreate={async (name) => {
          await handleCreateIncomeCategory(name);
        }}
        onRename={async (id, name) => {
          await handleRenameIncomeCategory(Number(id), name);
        }}
        onDelete={async (id) => {
          await handleDeleteIncomeCategory(Number(id));
        }}
      />
    </Box>
  );
};

export default BankImport;
